// ─────────────────────────────────────────────────────────────────────────────
// spell_layer.ts — Cast stroke visual feedback
//
// Minimal, ink-paper-blending visual for wand strokes. The previous
// version drove an animated consumption wavefront that ate through the
// stroke from start to end; that has been retired in favour of a
// quieter trace that hints at magic tension without disturbing the
// page composition.
//
// Visual model
// ────────────
//   active    — each stroke renders as a thin trace shimmering between
//               graphite and warm gold. A faint outer halo suggests
//               magic potential without saturating the surface. A
//               handful of slow gold motes drift off the pen tip.
//   resolving — finalize(valid):
//                 valid → strokes flash bright gold then fade out
//                 invalid → strokes fade quietly, no flare
//               In both cases the layer reports the result via
//               `onConsumed` *immediately* (synchronous with finalize)
//               so the spell action lands on the user's pen-up; the
//               flare is purely decorative and runs alongside.
//   idle      — nothing drawn.
//
// API surface kept compatible with the previous implementation: the
// canvas controller still calls startTracing / feedPoint / liftPen /
// beginNextStroke / finalize / reset, and reads state / penDown /
// allPoints / allStrokes / onConsumed / draw.
// ─────────────────────────────────────────────────────────────────────────────

import type { StrokePoint } from '@shared/notebook_model';

// ── Palette ─────────────────────────────────────────────────────────────────

const GRAPHITE = '#3a2a1c';     // ink baseline — sits in the page tone
const GOLD     = '#c89c3a';     // warm magic accent
const PALE     = '#f0d99a';     // resolve flare highlight

// ── Tunables ────────────────────────────────────────────────────────────────

/** Live cast stroke base trace width. */
const STROKE_WIDTH = 1.6;

/** Per-point tail-glow lifetime. The cast trace is built from a moving
 *  bright-gold halo on each freshly-laid point, fading to nothing
 *  over this window. Same effect family as InkLayer's initial-tail
 *  glow, but applied along the whole stroke for that signature
 *  "magic ink" feel. */
const CAST_TAIL_COOL_MS = 280;

/** Resolve flare duration. Brief — the spell action has already fired
 *  by the time the flare plays out. */
const RESOLVE_FLARE_MS = 320;

/** Overall fade-out duration after the flare. Short and clean. */
const RESOLVE_FADE_MS  = 260;

// ── Particle (sparse drift dust) ────────────────────────────────────────────

interface Mote {
  x: number; y: number;
  vx: number; vy: number;
  life: number;             // 0..1, decays each tick
  size: number;
  warm: boolean;            // true = gold, false = graphite
}

function makeMote(x: number, y: number, warm: boolean): Mote {
  const angle = Math.random() * Math.PI * 2;
  const speed = 0.10 + Math.random() * 0.18;
  return {
    x, y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed - 0.05,   // slight upward bias
    life: 1,
    size: 0.8 + Math.random() * 0.6,
    warm,
  };
}

// ── Stroke record ───────────────────────────────────────────────────────────

interface StrokeRecord {
  points: StrokePoint[];
}

// ── Public types ────────────────────────────────────────────────────────────

export type SpellState = 'idle' | 'active' | 'resolving';

// ── Layer ───────────────────────────────────────────────────────────────────

export class SpellLayer {
  private _ctx: CanvasRenderingContext2D;
  private _state: SpellState = 'idle';

  private _strokes: StrokeRecord[] = [];
  private _penDown = false;

  private _spellValid: boolean | null = null;
  private _resolveStartMs = 0;

  private _motes: Mote[] = [];
  private _moteCounter = 0;

  /** Fires immediately on `finalize(valid)`. Kept for API
   *  compatibility — the previous implementation deferred this until
   *  the consumption wavefront finished, but with the wavefront gone
   *  there's no reason to delay. */
  onConsumed: ((valid: boolean) => void) | null = null;

  constructor(ctx: CanvasRenderingContext2D) {
    this._ctx = ctx;
  }

  // ── Public API ─────────────────────────────────────────────────────────

  startTracing(): void {
    this._state = 'active';
    this._strokes = [{ points: [] }];
    this._penDown = true;
    this._spellValid = null;
    this._resolveStartMs = 0;
    this._motes = [];
    this._moteCounter = 0;
  }

  feedPoint(pt: StrokePoint): void {
    if (this._state !== 'active') return;
    const stroke = this._strokes[this._strokes.length - 1];
    stroke.points.push(pt);

    // Sparse motes — a small puff at first contact, then occasional
    // drift dust along the stroke. Cheap; capped naturally because we
    // only spawn every Nth point.
    if (stroke.points.length === 1 && this._strokes.length === 1) {
      for (let i = 0; i < 4; i++) this._motes.push(makeMote(pt.x, pt.y, true));
    }
    if (++this._moteCounter % 7 === 0) {
      this._motes.push(makeMote(pt.x, pt.y, this._moteCounter % 2 === 0));
    }
  }

  liftPen(): void {
    if (this._state !== 'active' || !this._penDown) return;
    this._penDown = false;
  }

  beginNextStroke(): void {
    if (this._state !== 'active') return;
    this._penDown = true;
    this._strokes.push({ points: [] });
  }

  /** Mark the cast as resolved. Fires onConsumed synchronously with
   *  the result — the resolve flare animates afterwards, decoratively. */
  finalize(valid: boolean): void {
    if (this._state !== 'active') return;
    this._spellValid = valid;
    if (this._penDown) this._penDown = false;
    this._state = 'resolving';
    this._resolveStartMs = performance.now();
    // A small extra puff on resolve so the eye registers something
    // happened on the page even when no flare fires (invalid case).
    if (this._strokes.length > 0) {
      const lastStroke = this._strokes[this._strokes.length - 1];
      const tip = lastStroke.points[lastStroke.points.length - 1];
      if (tip) {
        for (let i = 0; i < 5; i++) this._motes.push(makeMote(tip.x, tip.y, valid));
      }
    }
    this.onConsumed?.(valid);
  }

  reset(): void {
    this._state = 'idle';
    this._strokes = [];
    this._penDown = false;
    this._spellValid = null;
    this._resolveStartMs = 0;
    this._motes = [];
  }

  get state(): SpellState { return this._state; }
  get penDown(): boolean { return this._penDown; }

  get allPoints(): StrokePoint[] {
    const out: StrokePoint[] = [];
    for (const s of this._strokes) out.push(...s.points);
    return out;
  }

  get allStrokes(): StrokePoint[][] {
    return this._strokes.map(s => s.points.slice());
  }

  // ── Render ─────────────────────────────────────────────────────────────

  draw(pageX: number, pageY: number): void {
    if (this._state === 'idle' && this._motes.length === 0) return;

    const ctx = this._ctx;
    const now = performance.now();

    ctx.save();
    ctx.translate(pageX, pageY);

    if (this._state === 'active') {
      this._drawActiveStrokes(ctx, now, 1);
    } else if (this._state === 'resolving') {
      const elapsed = now - this._resolveStartMs;
      if (elapsed > RESOLVE_FLARE_MS + RESOLVE_FADE_MS) {
        // Visual finished. Drop into idle so subsequent draw calls are
        // no-ops (canvas_app drives reset on its own).
        this._state = 'idle';
        this._strokes = [];
      } else {
        this._drawResolving(ctx, now);
      }
    }

    this._tickAndDrawMotes(ctx);
    ctx.restore();
  }

  // ── Active state — graphite trace + bright gold tail-glow ─────────────

  private _drawActiveStrokes(
    ctx: CanvasRenderingContext2D, now: number, alphaMul: number,
  ): void {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // 1. Base trace — thin graphite line so the stroke reads on the
    //    page even after the gold tail has cooled.
    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = STROKE_WIDTH;
    ctx.globalAlpha = 0.85 * alphaMul;
    for (const stroke of this._strokes) {
      this._tracePath(ctx, stroke.points);
    }

    // 2. Gold tail-glow on every recently-laid point. Same per-point
    //    radial-gradient pop the user picked out as the magic look,
    //    bright and warm here (uses lighter blend) so the cast clearly
    //    reads as magical ink.
    ctx.globalCompositeOperation = 'lighter';
    for (const stroke of this._strokes) {
      const pts = stroke.points;
      for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        const age = now - p.timestamp;
        if (age > CAST_TAIL_COOL_MS) break;
        const heat = (1 - age / CAST_TAIL_COOL_MS) * alphaMul;
        const r = (STROKE_WIDTH * 1.1) * (1 + heat * 0.8);
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 1.6);
        grad.addColorStop(0,    `rgba(255, 213, 79, ${heat * 0.55})`);
        grad.addColorStop(0.55, `rgba(200, 168, 74, ${heat * 0.25})`);
        grad.addColorStop(1,    'rgba(60, 40, 10, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r * 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.restore();
  }

  // ── Resolve state — quick flare or quiet fade ──────────────────────────

  private _drawResolving(ctx: CanvasRenderingContext2D, now: number): void {
    const elapsed = now - this._resolveStartMs;
    const valid = this._spellValid === true;

    if (valid) {
      // Flare: bright pale-gold pulse during the first RESOLVE_FLARE_MS.
      if (elapsed < RESOLVE_FLARE_MS) {
        const t = elapsed / RESOLVE_FLARE_MS;
        const flareAlpha = (1 - Math.abs(t - 0.35) * 1.6);   // peaks at t≈0.35
        ctx.save();
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.shadowColor = PALE;
        ctx.shadowBlur = 9;
        ctx.strokeStyle = PALE;
        ctx.lineWidth = STROKE_WIDTH * 1.4;
        ctx.globalAlpha = Math.max(0, flareAlpha) * 0.9;
        for (const stroke of this._strokes) {
          this._tracePath(ctx, stroke.points);
        }
        ctx.restore();
      }
      // Underlying trace fades alongside the flare and through the
      // fade window after.
      const fadeT = elapsed / (RESOLVE_FLARE_MS + RESOLVE_FADE_MS);
      this._drawActiveStrokes(ctx, now, Math.max(0, 1 - fadeT));
    } else {
      // Invalid: just the trace, fading out quickly. No flare.
      const total = RESOLVE_FLARE_MS + RESOLVE_FADE_MS;
      const fadeT = elapsed / total;
      this._drawActiveStrokes(ctx, now, Math.max(0, 1 - fadeT) * 0.7);
    }
  }

  private _tracePath(
    ctx: CanvasRenderingContext2D, points: StrokePoint[],
  ): void {
    if (points.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
  }

  // ── Motes ──────────────────────────────────────────────────────────────

  private _tickAndDrawMotes(ctx: CanvasRenderingContext2D): void {
    if (this._motes.length === 0) return;
    ctx.save();
    const out: Mote[] = [];
    for (const m of this._motes) {
      m.x += m.vx;
      m.y += m.vy;
      m.vy += 0.005;          // gentle gravity
      m.life -= 0.02;
      if (m.life <= 0) continue;
      out.push(m);
      ctx.globalAlpha = m.life * 0.7;
      ctx.fillStyle = m.warm ? GOLD : GRAPHITE;
      ctx.beginPath();
      ctx.arc(m.x, m.y, m.size, 0, Math.PI * 2);
      ctx.fill();
    }
    this._motes = out;
    ctx.restore();
  }
}

